// Urban exploration rewards — web portal API client.
//
// Thin fetch wrappers over the /api/merchant/vouchers + /api/creator/scripts
// REST shims (server/index.js). These mirror the mobile GraphQL flows
// (MerchantVoucherAuthView / RouteRewardSetupView) and are authenticated with
// the organizer/merchant app-account token (same as the /organizer portal).

import { getAuthBaseUrl } from './authApi';
import { organizerHeaders } from './organizerApi';

async function request(path, { method = 'GET', body } = {}) {
  const b = getAuthBaseUrl();
  if (!b) return { success: false, error: 'API base not configured' };
  const res = await fetch(`${b}${path}`, {
    method,
    headers: organizerHeaders(),
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { success: false, error: data.error || `Request failed (${res.status})`, ...data };
  return data;
}

// --- Merchant voucher authentication + redemption --------------------------

export async function listMerchantVouchers(status = 'pending', limit = 50) {
  const q = `?status=${encodeURIComponent(status)}&limit=${limit}`;
  const data = await request(`/api/merchant/vouchers${q}`);
  return Array.isArray(data.vouchers) ? data.vouchers : [];
}

export function authenticateVoucher(voucherId, approve, note) {
  return request(`/api/merchant/vouchers/${encodeURIComponent(voucherId)}/authenticate`, {
    method: 'POST',
    body: { approve, note: note || null },
  });
}

export function redeemVoucherCode(code, note) {
  return request('/api/merchant/vouchers/redeem', {
    method: 'POST',
    body: { code, note: note || null },
  });
}

export async function searchMerchants(query, limit = 20) {
  const q = `?query=${encodeURIComponent(query || '')}&limit=${limit}`;
  const data = await request(`/api/merchant/search${q}`);
  return Array.isArray(data.merchants) ? data.merchants : [];
}

// --- Creator route reward creation / editing -------------------------------

export async function listMyScripts() {
  const data = await request('/api/creator/scripts');
  return Array.isArray(data.scripts) ? data.scripts : [];
}

export async function listScriptVouchers(scriptId) {
  const data = await request(`/api/creator/scripts/${encodeURIComponent(scriptId)}/vouchers`);
  return Array.isArray(data.vouchers) ? data.vouchers : [];
}

export function saveCompletionReward(scriptId, completionReward) {
  return request(`/api/creator/scripts/${encodeURIComponent(scriptId)}/completion-reward`, {
    method: 'PATCH',
    body: { completionReward },
  });
}

// --- Form option enums (mirror types.ts RouteRewardVoucher) -----------------

export const COUPON_TYPE_OPTIONS = [
  { value: 'free_item', label: '免费赠品 Free item' },
  { value: 'fixed_discount', label: '固定金额抵扣 Fixed discount' },
  { value: 'percentage', label: '百分比折扣 Percentage off' },
  { value: 'custom', label: '自定义 Custom' },
];

/** Convert a script's voucher list (from listScriptVouchers) into editable rows. */
export function vouchersToRows(vouchers) {
  const list = Array.isArray(vouchers) ? vouchers : [];
  return list.map((v) => ({
    id: v.id ?? null,
    title: v.title ?? '',
    description: v.description ?? '',
    couponType: v.couponType ?? 'free_item',
    valueDollars: v.valueCents != null ? String((Number(v.valueCents) / 100).toFixed(2)) : '',
    discountPercent: v.discountPercent != null ? String(v.discountPercent) : '',
    winnerLimit: v.winnerLimit != null ? String(v.winnerLimit) : '1',
    merchantUserId: v.merchantUserId ?? null,
    merchantBrandName: v.merchantBrandName ?? null,
    status: v.status ?? null,
  }));
}

/** Convert editable voucher rows back into the API payload shape. */
export function voucherRowsToPayload(rows) {
  return rows
    .filter((r) => String(r.title || '').trim())
    .map((r) => {
      const out = {
        id: r.id ?? null,
        title: String(r.title).trim(),
        description: String(r.description || '').trim() || null,
        couponType: r.couponType || 'free_item',
        winnerLimit: Math.max(1, Number(r.winnerLimit) || 1),
        merchantUserId: r.merchantUserId ?? null,
        merchantBrandName: r.merchantBrandName ?? null,
      };
      if (r.couponType === 'fixed_discount' || r.couponType === 'free_item') {
        const dollars = Number(r.valueDollars);
        if (Number.isFinite(dollars) && dollars > 0) out.valueCents = Math.round(dollars * 100);
      }
      if (r.couponType === 'percentage') {
        const pct = Number(r.discountPercent);
        if (Number.isFinite(pct) && pct > 0) out.discountPercent = Math.min(100, Math.round(pct));
      }
      return out;
    });
}
